import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Bot, Box, CheckCircle2, Code2, Cpu, Globe,
  Layers, Link2, Lock, Network, Puzzle, Server, Smartphone, Zap,
} from 'lucide-react';
import SEO from '../components/SEO';
import RelatedPages from '../components/RelatedPages';

const fade    = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } };
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.07 } } };

const CodeBlock = ({ children }) => (
  <pre className="bg-[#071020] border border-white/10 rounded-xl p-5 text-xs text-cyan-200 font-mono leading-relaxed overflow-x-auto my-6 whitespace-pre-wrap">
    {children}
  </pre>
);

const FeatureRow = ({ icon: Icon, title, desc, color = 'text-cyan-300' }) => (
  <div className="flex items-start gap-4 py-4 border-b border-white/6 last:border-0">
    <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
      <Icon className={`w-4 h-4 ${color}`} />
    </div>
    <div>
      <p className="text-white text-sm font-bold mb-1">{title}</p>
      <p className="text-slate-400 text-sm leading-relaxed">{desc}</p>
    </div>
  </div>
);

export default function DevelopersPage() {
  const [emailSent, setEmailSent] = useState(false);
  const [email, setEmail]         = useState('');
  const [sending, setSending]     = useState(false);

  const handleRequest = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/waitlist/developer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), source: 'developers-page' }),
      });
      setEmailSent(true);
    } catch { setEmailSent(true); }
    setSending(false);
  };

  return (
    <>
      <SEO
        title="Desarrolladores / MCP — Neuralia 369"
        description="Cómo Neuralia 369 expone y consume servidores MCP. Conecta tus propias herramientas al ecosistema de agentes. Android AppFunctions como equivalente on-device."
        path="/developers"
      />

      {/* ── HERO ── */}
      <section className="relative pt-28 pb-16 bg-[#02060f] border-b border-white/6 overflow-hidden" data-testid="developers-hero">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-1/3 w-[500px] h-[400px] bg-cyan-400/[0.04] rounded-full blur-[110px]" />
          <div className="absolute bottom-0 left-1/4  w-[350px] h-[300px] bg-violet-400/[0.03] rounded-full blur-[90px]" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={stagger} initial="hidden" animate="show">
            <motion.p variants={fade} className="editorial-kicker mb-5 text-cyan-300/80">
              Desarrolladores · MCP
            </motion.p>
            <motion.h1 variants={fade} className="text-white font-heading text-4xl sm:text-5xl font-black leading-tight mb-5" data-testid="developers-title">
              Conecta cualquier herramienta a{' '}
              <span className="bg-gradient-to-r from-cyan-300 to-blue-400 bg-clip-text text-transparent">Aynara</span>
              <br />vía MCP
            </motion.h1>
            <motion.p variants={fade} className="text-slate-300 text-lg max-w-2xl leading-relaxed mb-8">
              Model Context Protocol (MCP) es el estándar abierto que permite a los agentes de IA descubrir
              y usar herramientas externas de forma estructurada. Neuralia 369 es un cliente y servidor MCP nativo.
            </motion.p>
            <motion.div variants={fade} className="flex flex-wrap gap-3">
              {['MCP Client nativo', 'MCP Server expuesto', 'Android AppFunctions', 'Permisos por herramienta'].map(tag => (
                <span key={tag} className="text-[10px] font-bold uppercase tracking-widest border border-cyan-400/25 bg-cyan-400/8 text-cyan-300 rounded-full px-3 py-1">{tag}</span>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── QUÉ ES MCP ── */}
      <section className="py-16 bg-[#030a17] border-b border-white/6" data-testid="developers-mcp-explainer">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }}>
              <p className="editorial-kicker mb-4 text-slate-400">Qué es MCP</p>
              <h2 className="text-white font-heading text-3xl font-black mb-5">
                El USB-C de los agentes de IA
              </h2>
              <p className="text-slate-300 text-base leading-relaxed mb-4">
                Model Context Protocol (MCP) es un estándar abierto, propuesto por Anthropic y adoptado por el
                ecosistema, que define cómo un modelo de lenguaje descubre, invoca y obtiene resultados de
                herramientas externas. Piensa en él como una interfaz estandarizada: igual que USB-C conecta
                cualquier dispositivo a cualquier cargador, MCP conecta cualquier herramienta a cualquier agente.
              </p>
              <p className="text-slate-300 text-base leading-relaxed mb-4">
                Sin MCP, cada integración requiere código específico por herramienta y por agente.
                Con MCP, el agente pregunta "¿qué herramientas tienes disponibles?" y la herramienta responde
                con su esquema. El agente las usa sin que nadie haya escrito código de integración ad-hoc.
              </p>
              <p className="text-slate-300 text-base leading-relaxed">
                Un servidor MCP expone herramientas (funciones que el agente puede llamar), recursos (datos
                que el agente puede leer) y prompts (plantillas para guiar al modelo). Un cliente MCP
                —como Aynara— descubre esos elementos y los usa dentro del flujo del agente.
              </p>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.55 }}>
              <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-6">
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-4">Flujo MCP básico</p>
                <div className="space-y-3">
                  {[
                    { step: '01', actor: 'Aynara (MCP Client)', desc: 'Descubre los servidores MCP configurados para el agente', color: 'text-cyan-300' },
                    { step: '02', actor: 'Servidor MCP',        desc: 'Responde con lista de herramientas, recursos y prompts disponibles', color: 'text-emerald-300' },
                    { step: '03', actor: 'Aynara',              desc: 'El modelo selecciona la herramienta correcta para la tarea', color: 'text-cyan-300' },
                    { step: '04', actor: 'Servidor MCP',        desc: 'Ejecuta la herramienta y devuelve el resultado estructurado', color: 'text-emerald-300' },
                    { step: '05', actor: 'Aynara',              desc: 'Usa el resultado en el contexto del agente y continúa el flujo', color: 'text-cyan-300' },
                  ].map(({ step, actor, desc, color }) => (
                    <div key={step} className="flex items-start gap-3">
                      <span className="text-[10px] font-black text-slate-600 pt-1 flex-shrink-0">{step}</span>
                      <div>
                        <span className={`text-xs font-bold ${color}`}>{actor}</span>
                        <p className="text-xs text-slate-400 mt-0.5">{desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── NEURALIA 369 y MCP ── */}
      <section className="py-16 bg-[#02060f] border-b border-white/6" data-testid="developers-neuralia-mcp">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} className="text-center mb-10">
            <p className="editorial-kicker mb-3 text-slate-400">Neuralia 369 y MCP</p>
            <h2 className="text-white font-heading text-3xl font-black">
              Cliente y servidor al mismo tiempo
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-5 mb-10">
            {/* MCP Client */}
            <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }}
              className="rounded-2xl border border-cyan-400/25 bg-cyan-400/6 p-6" data-testid="dev-client-card">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-xl bg-cyan-400/15 border border-cyan-400/30 flex items-center justify-center">
                  <Network className="w-4 h-4 text-cyan-300" />
                </div>
                <p className="text-[10px] font-black uppercase tracking-widest text-cyan-300">Neuralia como MCP Client</p>
              </div>
              <p className="text-slate-300 text-sm leading-relaxed mb-4">
                Aynara puede conectarse a cualquier servidor MCP externo y usar sus herramientas
                dentro del flujo del agente. Si tu empresa ya tiene un servidor MCP que expone
                acceso a tu ERP, tu base de datos interna o tu sistema de tickets, Aynara
                lo consume sin necesitar código de integración personalizado.
              </p>
              <ul className="space-y-2">
                {['Descubrimiento automático de herramientas disponibles', 'Invocación con permisos por agente y por herramienta', 'Resultado auditado en el log de acciones del agente'].map(t => (
                  <li key={t} className="flex items-start gap-2 text-xs text-slate-300">
                    <CheckCircle2 className="w-3 h-3 text-cyan-400 flex-shrink-0 mt-0.5" /> {t}
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* MCP Server */}
            <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }}
              className="rounded-2xl border border-emerald-400/25 bg-emerald-400/6 p-6" data-testid="dev-server-card">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-xl bg-emerald-400/15 border border-emerald-400/30 flex items-center justify-center">
                  <Server className="w-4 h-4 text-emerald-300" />
                </div>
                <p className="text-[10px] font-black uppercase tracking-widest text-emerald-300">Neuralia como MCP Server</p>
              </div>
              <p className="text-slate-300 text-sm leading-relaxed mb-4">
                Neuralia 369 también expone sus propias operaciones como un servidor MCP.
                Esto significa que un agente externo —desplegado en Claude, Cursor, Windsurf
                o cualquier cliente MCP compatible— puede usar los agentes y herramientas
                de Neuralia 369 como si fueran herramientas propias.
              </p>
              <ul className="space-y-2">
                {['Herramientas expuestas: leads, facturas, agenda, CRM', 'Autenticación por Auth Link revocable', 'Compatible con cualquier cliente MCP estándar'].map(t => (
                  <li key={t} className="flex items-start gap-2 text-xs text-slate-300">
                    <CheckCircle2 className="w-3 h-3 text-emerald-400 flex-shrink-0 mt-0.5" /> {t}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          {/* Code example */}
          <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }}>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-3">Ejemplo: conectar un servidor MCP externo a Aynara</p>
            <CodeBlock>{`// neuralia369.config.js — configuración de servidores MCP
{
  "mcp_servers": [
    {
      "name": "mi-erp",
      "url": "https://erp.miempresa.com/mcp",
      "auth": "bearer",
      "auth_link_id": "lnk_xxxxxxxxxxxx",   // Auth Link revocable desde el panel
      "allowed_agents": ["ventas", "crm"],   // Qué agentes pueden usar este servidor
      "tools_whitelist": ["get_order", "create_invoice"]  // Principio de mínimo privilegio
    }
  ]
}`}
            </CodeBlock>
          </motion.div>
        </div>
      </section>

      {/* ── CONECTAR TUS HERRAMIENTAS ── */}
      <section className="py-16 bg-[#030a17] border-b border-white/6" data-testid="developers-connect-tools">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }}>
              <p className="editorial-kicker mb-4 text-violet-300/80">Para partners técnicos</p>
              <h2 className="text-white font-heading text-3xl font-black mb-5">
                Conecta tus herramientas en 4 pasos
              </h2>
              <div className="space-y-5">
                {[
                  { n: '01', title: 'Implementa un servidor MCP',       desc: 'Expón tus herramientas usando el SDK oficial de MCP (Python, TypeScript o cualquier lenguaje con implementación del protocolo). Define las herramientas con su esquema de entrada y salida.', color: 'text-violet-300' },
                  { n: '02', title: 'Configura el Auth Link',           desc: 'Desde el panel de Neuralia 369, genera un Auth Link específico para este servidor MCP. El Auth Link actúa como token de autorización y puede revocarse en cualquier momento sin afectar otros.', color: 'text-violet-300' },
                  { n: '03', title: 'Registra el servidor en Neuralia', desc: 'Añade la URL de tu servidor MCP y el Auth Link en la configuración del workspace. Define qué agentes pueden usarlo y qué herramientas están en whitelist.', color: 'text-violet-300' },
                  { n: '04', title: 'Verifica en el entorno de prueba', desc: 'Usa el entorno sandbox para verificar que el agente descubre y usa correctamente tus herramientas antes de activarlo en producción.', color: 'text-violet-300' },
                ].map(({ n, title, desc, color }) => (
                  <div key={n} className="flex items-start gap-4">
                    <span className={`text-2xl font-black ${color} opacity-40 leading-none flex-shrink-0 w-8`}>{n}</span>
                    <div>
                      <p className="text-white text-sm font-bold mb-1">{title}</p>
                      <p className="text-slate-400 text-sm leading-relaxed">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.55 }}>
              <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-6">
                <FeatureRow icon={Puzzle} title="SDK agnóstico de lenguaje" desc="El protocolo MCP tiene implementaciones en Python, TypeScript, Java y Rust. Cualquier backend que hable HTTP puede implementarlo." color="text-cyan-300" />
                <FeatureRow icon={Lock} title="Permisos granulares por herramienta" desc="Cada herramienta expuesta puede tener su propio nivel de autorización. No todas las herramientas necesitan el mismo Auth Link." color="text-violet-300" />
                <FeatureRow icon={Layers} title="Versionado de esquemas" desc="Los servidores MCP pueden versionar sus herramientas. Aynara gestiona la compatibilidad entre versiones sin interrumpir el flujo." color="text-emerald-300" />
                <FeatureRow icon={Globe} title="Herramientas locales y remotas" desc="Un servidor MCP puede estar en la nube, en una VPN corporativa o en localhost. La conexión se configura por URL." color="text-amber-300" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── ANDROID APPFUNCTIONS ── */}
      <section className="py-16 bg-[#02060f] border-b border-white/6" data-testid="developers-appfunctions">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }}>
              <p className="editorial-kicker mb-4 text-amber-300/80">Android · On-device</p>
              <h2 className="text-white font-heading text-3xl font-black mb-5">
                AppFunctions: el equivalente<br />on-device de MCP
              </h2>
              <p className="text-slate-300 text-base leading-relaxed mb-4">
                Android AppFunctions (parte de Android 16+) permite a los modelos de IA on-device
                —como Gemini Nano— descubrir y llamar funciones expuestas por apps instaladas en el
                dispositivo, de la misma forma que MCP lo hace en la nube.
              </p>
              <p className="text-slate-300 text-base leading-relaxed mb-4">
                La app móvil de Aynara (actualmente en diseño) implementará AppFunctions para
                que un agente pueda, por ejemplo, abrir la cámara, leer un QR de contrato,
                iniciar una llamada o aprobar una acción desde el dispositivo —sin que los datos
                salgan del teléfono.
              </p>
              <p className="text-slate-300 text-base leading-relaxed mb-6">
                Es el mismo principio que MCP aplicado al contexto on-device: el agente pregunta
                qué operaciones están disponibles en el dispositivo, el sistema responde con el
                esquema de AppFunctions instaladas, y el agente las invoca de forma estructurada.
              </p>
              <Link to="/app-movil" className="inline-flex items-center gap-2 text-sm font-semibold text-amber-300 hover:text-white transition-colors group" data-testid="dev-app-movil-link">
                Ver más sobre la app móvil <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>

            <motion.div initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.55 }}>
              <div className="rounded-2xl border border-amber-400/20 bg-amber-400/5 p-7">
                <div className="flex items-center gap-3 mb-5">
                  <Smartphone className="w-6 h-6 text-amber-300" />
                  <p className="text-[10px] font-black uppercase tracking-widest text-amber-300">MCP vs AppFunctions</p>
                </div>
                <div className="space-y-4">
                  {[
                    { label: 'Contexto',        mcp: 'En la nube / servidor',          appfn: 'On-device / Android' },
                    { label: 'Descubrimiento',  mcp: 'El cliente MCP llama a /list',   appfn: 'El modelo llama a AppFunctionService' },
                    { label: 'Invocación',      mcp: 'POST /call con esquema JSON',    appfn: 'AppFunctionCall con parámetros tipados' },
                    { label: 'Privacidad',      mcp: 'Datos viajan al servidor',       appfn: 'Datos permanecen en el dispositivo' },
                    { label: 'Agente base',     mcp: 'Cualquier LLM (GPT, Claude...)', appfn: 'Gemini Nano (Android 16+)' },
                  ].map(({ label, mcp, appfn }) => (
                    <div key={label} className="grid grid-cols-3 gap-2 text-xs">
                      <span className="text-slate-500 font-semibold">{label}</span>
                      <span className="text-slate-300">{mcp}</span>
                      <span className="text-amber-300 font-semibold">{appfn}</span>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-slate-600 mt-5 border-t border-white/6 pt-3">
                  AppFunctions requiere Android 16+ y la app de Aynara instalada. En diseño actualmente.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── CTA FINAL ── */}
      <section className="py-20 bg-[#030b18]" data-testid="developers-cta">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }}>
            <motion.div variants={fade} className="w-12 h-12 rounded-2xl bg-cyan-400/15 border border-cyan-400/30 flex items-center justify-center mx-auto mb-5">
              <Code2 className="w-6 h-6 text-cyan-300" />
            </motion.div>
            <motion.h2 variants={fade} className="text-white font-heading text-3xl font-black mb-4">
              Solicitar acceso al entorno de desarrolladores
            </motion.h2>
            <motion.p variants={fade} className="text-slate-300 text-base mb-7 max-w-md mx-auto">
              Acceso al sandbox MCP, documentación de herramientas expuestas y soporte técnico directo.
              Disponible para partners técnicos y clientes enterprise.
            </motion.p>
            {!emailSent ? (
              <motion.form variants={fade} onSubmit={handleRequest} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="tu@empresa.com"
                  className="flex-1 bg-white/[0.06] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400/50"
                  data-testid="dev-email-input" />
                <button type="submit" disabled={sending} className="btn-primary" data-testid="dev-cta-submit">
                  {sending ? 'Enviando…' : <><ArrowRight className="w-4 h-4" /> Solicitar acceso</>}
                </button>
              </motion.form>
            ) : (
              <motion.div variants={fade} className="flex items-center justify-center gap-2 text-emerald-300 font-semibold" data-testid="dev-success-msg">
                <CheckCircle2 className="w-5 h-5" /> Solicitud recibida. Te contactaremos pronto.
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>
      <RelatedPages pages={['sistema-multiagente','app-movil','tendencias','recursos']} />
    </>
  );
}
